import React, { ReactElement, useMemo, CSSProperties } from "react";
import { SmallBody, SmallBodyBolder, CaptionBody } from "src/components/Text";
import { QualityHubTaskListPanelItemProps } from './QualityHubTaskListPanelItem.types';
import { Conditional } from "src/components/Conditional";
import { Link } from "react-router-dom";
import ProgressTrackerCircle from "src/components/ProgressTrackerCircle";
import Visage2Icon from "src/visage2/Visage2Icon/Visage2Icon";
import { UnstyledButton } from "src/ui";
import { localize } from "src/l10n";
import { useSelector } from "react-redux";

/**
 * QualityHubTaskListPanelItem component
 *
 * @param {QualityHubTaskListPanelItemProps} props - The component props
 * @returns {ReactElement | null} The rendered component
 */
function QualityHubTaskListPanelItem({
    item,
    onAddClick,
}: QualityHubTaskListPanelItemProps): ReactElement | null {
    const {
        taskCount,
        completedCount = 0,
    } = item;

    const progressFill = useMemo<CSSProperties>(() => ({
        width: taskCount > 0 ? `${(completedCount / taskCount) * 100}%` : "0%",
    }), [completedCount, taskCount]);

    const canAdd = item.canAddTasks && onAddClick;

    const canOverrideLists = useSelector<Spintr.AppState, boolean>(
        (state) =>
            !state.ui.isSmallViewMode &&
            (state.profile.active.leadsDepartment ||
                state.profile.active.leadsOffice ||
                state.profile.active.leadsCompany)
    );

    return (
        <Link className="QualityHubTaskListPanelItem lightPrimaryBGColorHover"
              title={item.label}
              to={`/quality-hub/${item.id}`}>
            <Conditional condition={completedCount === taskCount}>
                <div className="QualityHubTaskListPanelItem-icon">
                    <ProgressTrackerCircle size={26} maxValue={1} currentValue={1} />
                </div>
            </Conditional>

            <div className="QualityHubTaskListPanelItem-info">

                <div className="QualityHubTaskListPanelItem-text">
                    <div className="QualityHubTaskListPanelItem-text-header">
                        <SmallBodyBolder color="contentDark">
                            {item.label}
                        </SmallBodyBolder>
                        <Conditional condition={item.allowOverride && canOverrideLists}>
                            <Link className="lightPrimaryBGColorHover"
                                  to={`/quality-hub/${item.id}/customize`}>
                                <CaptionBody color="blue">
                                    {localize("CUSTOMIZE_LIST")}
                                </CaptionBody>
                            </Link>
                        </Conditional>
                    </div>
                    <Conditional condition={!!item.description}>
                        <SmallBody color="contentNormal">
                            {item.description}
                        </SmallBody>
                    </Conditional>
                </div>

                <Conditional condition={taskCount > 0 && taskCount > completedCount}>
                    <div className="QualityHubTaskListPanelItem-progress">
                        <div className="QualityHubTaskListPanelItem-progressBar">
                            <div className="QualityHubTaskListPanelItem-progressBarFill"
                                 style={progressFill} />
                        </div>
                        <div className="QualityHubTaskListPanelItem-progressText">
                            <CaptionBody color="contentNormal">
                                {completedCount} / {taskCount}
                            </CaptionBody>
                        </div>
                    </div>
                </Conditional>
            </div>

            <Conditional condition={canAdd}>
                <div className="QualityHubTaskListPanelItem-actions">
                    <UnstyledButton className="QualityHubTaskListPanelItem-addButton"
                                    onClick={onAddClick}
                                    onClickData={item}>
                        <Visage2Icon icon="add" />
                    </UnstyledButton>
                </div>
            </Conditional>
        </Link>
    );
}

export default QualityHubTaskListPanelItem;

